import { useParams, useLocation } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { formatDate } from '@/helpers/formatDate';
import { hasBetaProductName } from '@/utils/subscriptionHelpers';
import { GenesisEventCelebration } from '@/features/genesisEvent';
import { useDesktopNoteHandoff } from '@/hooks/useDesktopNoteHandoff';
import { DesktopHandoffInterstitial, NoteLayout } from './components';
import { useNotePageData } from './hooks/useNotePageData';

const Note = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // 600px and below - true mobile
  const { noteId } = useParams();
  const location = useLocation();
  const isAudioRecording = (location.state as { isAudioRecording?: boolean } | null)?.isAudioRecording ?? false;
  const {
    noteData,
    signedUrl,
    signedThumbnailUrl,
    isOwner,
    userData,
    isProcessing,
    isNoteProcessing,
    processingTitleVisible,
    isLoadingNote,
    isLoadingTranscript,
    isLoadingFullTranscript,
    fullTranscriptList,
    isAudioOnly,
    noteTitle,
    noteSummary,
    shouldShowShareArrow,
    validatedSummaryFeedback,
    refetchNote,
    handleTitleUpdate,
    handleDocumentChaptersUpdate,
    handleKeyPointsUpdate,
    handleContentAnalysisUpdate,
    handleFeedbackChange,
    trackPlay,
  } = useNotePageData(noteId);

  // Offer the note to the desktop app when this computer has one that can take
  // it, and only for notes someone else shared with you — never your own.
  // Purely additive: the layout below renders and stays usable whatever the
  // handoff does. Recording and uploading live on their own routes (/record,
  // /audio-recording, /beta/*), each of which blocks navigation while active, so
  // neither can be running in this tab while the note page is mounted.
  const handoff = useDesktopNoteHandoff(noteId, noteData?.creator_user_id);

  return (
    <>
      {/* Genesis Event Celebration - triggers on first note view from plugin */}
      <GenesisEventCelebration noteId={noteId || ''} noteCreatorUserId={noteData?.creator_user_id} />

      <DesktopHandoffInterstitial {...handoff} />

      <NoteLayout
        isMobile={isMobile}
        noteTitle={noteTitle}
        noteSummary={noteSummary}
        noteDate={formatDate(noteData?.created_at || '')}
        isOwner={isOwner}
        isProcessing={isProcessing}
        isNoteProcessing={isNoteProcessing}
        processingTitleVisible={processingTitleVisible}
        isLoadingNote={isLoadingNote}
        onTitleUpdate={handleTitleUpdate}
        noteData={noteData}
        shouldShowShareArrow={shouldShowShareArrow}
        signedUrl={signedUrl}
        signedThumbnailUrl={signedThumbnailUrl}
        isAudioOnly={isAudioOnly || (isAudioRecording && isNoteProcessing)}
        trackPlay={trackPlay}
        isLoadingTranscript={isLoadingTranscript}
        isLoadingFullTranscript={isLoadingFullTranscript}
        fullTranscriptList={fullTranscriptList}
        refetchNote={refetchNote}
        onDocumentChaptersUpdate={handleDocumentChaptersUpdate}
        onKeyPointsUpdate={handleKeyPointsUpdate}
        onContentAnalysisUpdate={handleContentAnalysisUpdate}
        showSummaryFeedback={isOwner && Boolean(noteId) && hasBetaProductName(userData)}
        noteId={noteId}
        validatedSummaryFeedback={validatedSummaryFeedback}
        onFeedbackChange={handleFeedbackChange}
      />
    </>
  );
};

export default Note;
